<template>
  <div>
    <!-- Image -->
    <DsSectionImage
        v-if="pageContent.image"
        :image="pageContent.image"
        :screen="screen"
        class="ds-margin-bottom-48 ds-md-margin-bottom-0"
        is-contained
    />
    <!-- Page Navigation -->
    <div
        v-if="pageNavigationList.length"
        class="sticky-top ds-width-full"
    >
      <DsOnPageNavigation
          disposition="horizontal"
          :items="pageNavigationList"
          :screen="screen"
          :title="labels.onThisPage"
          update-route
          :button="downloadPdfButton"
          portal-target="header"
          :initial-active-item="routeHash"
          :event-data="{ 'dropdown-opened': { setScrollTop: null } }"
          use-grid
      />
    </div>
    <!-- Highlights -->
    <section id="highlights" data-id="highlights">
      <InvestorRelationsFinancialBlockCollection
          v-if="pageContent.financialData"
          :financial-data="pageContent.financialData"
          :usp-data="pageContent.highlightsUsps"
          :footnote="pageContent.highlightsFootnote"
          :year="pageContent.year"
          :show-line-chart="true"
          theme="dark"
          class="ds-padding-y-48 ds-md-padding-y-96"
      />
    </section>
    <ClientOnly>
    <section id="letter" data-id="letter">
      <template v-if="letterContent.length > 0">
        <div
            v-for="item in letterContent"
            :key="`financial-item-${item.id}`"
        >
          <component
              :is="item.component"
              :key="`letter-item-${item.id}`"
              v-bind="item.content"
          >
            <template
                v-if="item.id === letterContent.length -1 && pageContent.signatureItems.length > 0"
                #signatures
            >
              <InvestorRelationsShareholderLetterSignature :items="pageContent.signatureItems" />
            </template>
          </component>
          </div>
      </template>
    </section>

    <!-- Financial Items -->
    <section id="financial-results" data-id="financial-results">
      <template v-if="financialContent.length > 0">
        <div
            v-for="item in financialContent"
            :key="`financial-item-${item.id}`"
        >
<!--          {{ item.content }}-->

          <component
              :is="item.component"
              v-bind="item.content"
              :with-divider="item.id === financialContent.length-1"
              :with-title-size-large="true"
          >
            <!-- show signatures after the last item-->

            <template
                v-if="item.id === financialContent.length-1 && pageContent.signatureItems.length > 0"
                #signatures
            >
              <InvestorRelationsShareholderLetterSignature :items="pageContent.signatureItems" />
            </template>
          </component>
        </div>
      </template>
    </section>
    </ClientOnly>
    <!-- Growth Pillar Items -->
    <section id="growth-pillars" data-id="growth-pillars">
      <template v-if="pageContent.growthPillarItems.length">
        <InvestorRelationsShareholderLetterGrowthPillarBlock
            v-for="(growthPillarItem, growthPillarItemIndex) in pageContent.growthPillarItems"
            :key="`growth-pillar-item-${growthPillarItemIndex}`"
            v-bind="growthPillarItem"
            :with-divider="growthPillarItemIndex !== pageContent.growthPillarItems.length-1"
        />
      </template>
    </section>
    <!-- Financial statements -->
    <div id="financial-statements" data-id="financial-statements">
      <DsCallToAction
          v-if="financialStatements"
          v-bind="financialStatements"
          alignment="left"
          with-title-size-large
      />
    </div>
    <!-- Notice -->
    <section v-if="pageContent.notice">
      <div class="ds-container ds-padding-y-48 ds-md-padding-y-96">
        <div class="ds-grid ds-justify-content-center">
          <div class="ds-grid__col ds-grid__col--12 ds-md-grid__col--8">
            <RichText :data="pageContent.notice" />
          </div>
        </div>
      </div>
    </section>
  </div>
</template>
<script setup>
import { DsCallToAction, DsOnPageNavigation, DsSectionImage, DsImageElement, DsFigureElement } from 'awds'

const InvestorRelationsShareholderLetterGrowthPillarBlock = defineAsyncComponent(() => import("../investor-relations-shareholder-letter-growth-pillar-block.vue"));
const InvestorRelationsShareholderLetterSignature = defineAsyncComponent(() => import("../investor-relations-shareholder-letter-signature.vue"));
const InvestorRelationsShareholderLetterContentBlock = defineAsyncComponent(() => import("../investor-relations-shareholder-letter-content-block.vue"));
const InvestorRelationsShareholderLetterFteBlock = defineAsyncComponent(()=> import("../investor-relations-shareholder-letter-fte-block.vue"))
const InvestorRelationsShareholderLetterHighlightsBlock = defineAsyncComponent(() => import("../investor-relations-shareholder-letter-highlights-block.vue"))

const { localeGlobalContentData } = useGlobalData()
const { screen } = useScreen()
const { labels } = useInvestorsGlobalData()
const { setTheme }  = useTheme()
const route = useRoute()

const routeHash = route.hash

const props = defineProps({
  pageContent: {
    type: Object,
    default: () => null
  }
})

const pageNavigationList = computed (() => {
  return [
    ...(props.pageContent.financialData ? [{ label: labels.value.shareholderLetterHighlights || 'Highlights', slug: 'highlights' }] : []),
    ...(props.pageContent.letterItems.length ? [{ label: labels.value.shareholderLetterLetter || 'Letter', slug: 'letter' }] : []),
    ...(props.pageContent.financialItems.length ? [{ label: labels.value.shareholderLetterFinancialResults || 'Financial Results', slug: 'financial-results' }] : []),
    ...(props.pageContent.growthPillarItems.length ? [{ label: labels.value.shareholderLetterGrowthPillars || 'Growth Pillars', slug: 'growth-pillars' }] : []),
    ...(props.pageContent.financialStatementsTitle ? [{ label: labels.value.shareholderLetterFinancialStatements || 'Financial Statements', slug: 'financial-statements' }] : [])
  ]
})

const financialStatements = computed (() => {
  if (!props.pageContent.downloadPdf) {
    return null
  }
  return {
    title: props.pageContent.financialStatementsTitle,
    subtitle: `${props.pageContent.period} ${labels.value.companyLegalName}`,
    primaryLink: {
      ...props.pageContent.downloadPdf,
      iconLeft: { name: 'download', category: 'arrows' }
    }
  }
})

const downloadPdfButton = computed(() => {
  if (!props.pageContent.downloadPdf) {
    return null
  }
  return {
    ...props.pageContent.downloadPdf,
    iconRight: { category: 'arrows', name: 'download' }
  }
})

const shareHolderLetterComponentsMap = new Map([
  ["InvestorRelationsShareholderLetterGrowthPillarBlock",
    InvestorRelationsShareholderLetterGrowthPillarBlock],
  ["InvestorRelationsShareholderLetterFteBlock",
      InvestorRelationsShareholderLetterFteBlock],
  ["InvestorRelationsShareholderLetterHighlightsBlock",
    InvestorRelationsShareholderLetterHighlightsBlock],
  [ "InvestorRelationsShareholderLetterContentBlock",
    InvestorRelationsShareholderLetterContentBlock],
])

const letterItems = computed(() => {
  return props?.pageContent?.letterItems
})

const letterContent = computed(() => {
  return letterItems.value.map(((i, index) => ({
    ...i,
    id: index++,
    component: shareHolderLetterComponentsMap.get(i.componentName)
  })))
})

const financialItems = computed(() => {
  return props?.pageContent?.financialItems
})


const financialItemsComponentsMap = new Map([
  ["DsImageElement", DsImageElement],
  ["DsFigureElement", DsFigureElement],
  [ "InvestorRelationsShareholderLetterContentBlock",
    InvestorRelationsShareholderLetterContentBlock],
]);

const financialContent = (financialItems.value.map((i, index) => ({
  ...i,
  id: index++,
  component: financialItemsComponentsMap.get(i.componentName)
})))

</script>
